import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./Villaroompricing.css";

const API_BASE = (
  process.env.REACT_APP_API_URL || "https://bmtvendorvendorfinal.onrender.com"
).replace(/\/$/, "");

// same keys as the Inventory page so the villa / room choice carries over
const KEY_VILLA = "bmtVillaInvVilla";
const KEY_ROOM = "bmtVillaInvRoom";

/* ---------- helpers ---------- */

const pad = (n) => String(n).padStart(2, "0");
const todayKey = () => {
  const n = new Date();
  return `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}`;
};
const dkey = (v) => (v ? String(v).slice(0, 10) : "");
const toUTC = (k) => {
  const [y, m, d] = k.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
};
const nightsBetween = (a, b) => Math.round((toUTC(b) - toUTC(a)) / 86400000) + 1;
const fmtDate = (k) =>
  k
    ? new Date(toUTC(k)).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" })
    : "—";
const fmtDateTime = (v) =>
  v ? new Date(v).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";
const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const readSession = (key) => {
  try { return sessionStorage.getItem(key) || ""; } catch { return ""; }
};
const writeSession = (key, value) => {
  try { sessionStorage.setItem(key, value); } catch { /* ignore */ }
};

const isSeasonal = (r) => Boolean(dkey(r.startDate) && dkey(r.endDate));

const ruleState = (r, today) => {
  if (!r.isActive) return "disabled";
  if (!isSeasonal(r)) return "always";
  if (today < dkey(r.startDate)) return "upcoming";
  if (today > dkey(r.endDate)) return "expired";
  return "live";
};

const STATE_LABEL = {
  always: "Always on",
  live: "Live now",
  upcoming: "Upcoming",
  expired: "Expired",
  disabled: "Disabled",
};

const guestText = (r) =>
  `${r.adults} adult${r.adults === 1 ? "" : "s"}${r.children ? ` + ${r.children} child${r.children === 1 ? "" : "ren"}` : ""}`;

async function apiRequest(path, options = {}) {
  const token = localStorage.getItem("vendorToken");

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...(options.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok || data.success === false) {
    throw new Error(
      data.message ||
        (response.status === 401
          ? "Session expired. Please log in again."
          : `Request failed (${response.status})`)
    );
  }
  return data;
}

/* which active rules clash with each other (same dates / two base rates) */
function findConflicts(rules) {
  const active = rules.filter((r) => r.isActive);
  const map = {};
  const add = (id, other) => {
    map[id] = map[id] || [];
    map[id].push(other);
  };

  for (let i = 0; i < active.length; i += 1) {
    for (let j = i + 1; j < active.length; j += 1) {
      const a = active[i];
      const b = active[j];
      const aS = isSeasonal(a);
      const bS = isSeasonal(b);
      let clash = false;
      if (!aS && !bS) clash = true;
      else if (aS && bS) clash = dkey(a.startDate) <= dkey(b.endDate) && dkey(b.startDate) <= dkey(a.endDate);
      if (clash) {
        add(a._id, b);
        add(b._id, a);
      }
    }
  }
  return map;
}

/* ---------- ADD / EDIT PAGE ---------- */

const seedForm = (rule, room) => {
  if (rule) {
    return {
      type: isSeasonal(rule) ? "seasonal" : "base",
      start: dkey(rule.startDate),
      end: dkey(rule.endDate),
      price: String(rule.pricePerNight ?? ""),
      maxGuests: String(rule.maxGuests ?? 2),
      adults: String(rule.adults ?? 2),
      children: String(rule.children ?? 0),
      extra: String(rule.extraGuestPrice ?? 0),
    };
  }
  const maxGuests = Math.max(1, Number(room?.maxGuests) || 2);
  const adults = Math.min(Math.max(1, Number(room?.maxAdults) || 2), maxGuests);
  const children = Math.min(Number(room?.maxChildren) || 0, maxGuests - adults);
  return {
    type: "base",
    start: "",
    end: "",
    price: room?.pricePerNight !== undefined ? String(room.pricePerNight) : "",
    maxGuests: String(maxGuests),
    adults: String(adults),
    children: String(children),
    extra: String(room?.extraGuestAllowed ? room.extraGuestCharge || 0 : 0),
  };
};

function PricingForm({ mode, rule, room, rules, basePath, onCancel, onSaved }) {
  const isEdit = mode === "edit";
  const topRef = useRef(null);
  const [f, setF] = useState(() => seedForm(rule, room));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    topRef.current?.scrollIntoView({ block: "start" });
  }, []);

  const set = (key, value) => setF((p) => ({ ...p, [key]: value }));
  const bind = (key) => ({ value: f[key], onChange: (e) => set(key, e.target.value) });

  const num = (k) => Number(f[k]);
  const validate = () => {
    if (f.price === "" || !Number.isFinite(num("price")) || num("price") < 0)
      return "Please enter a valid price per night.";
    if (!Number.isInteger(num("maxGuests")) || num("maxGuests") < 1)
      return "Maximum guests must be a whole number of at least 1.";
    if (!Number.isInteger(num("adults")) || num("adults") < 1)
      return "Adults must be a whole number of at least 1.";
    if (!Number.isInteger(num("children")) || num("children") < 0)
      return "Children must be a whole number (0 or more).";
    if (num("adults") + num("children") > num("maxGuests"))
      return "Adults + children cannot be more than the maximum guests.";
    if (f.extra === "" || !Number.isFinite(num("extra")) || num("extra") < 0)
      return "Extra guest price must be 0 or more.";
    if (f.type === "seasonal") {
      if (!f.start || !f.end) return "Please select both start and end dates.";
      if (f.start > f.end) return "The start date must be on or before the end date.";
    }
    return "";
  };

  const datesOk = f.type === "seasonal" && f.start && f.end && f.start <= f.end;

  const conflicts = useMemo(() => {
    const others = rules.filter((r) => r.isActive && r._id !== (isEdit ? rule?._id : null));
    if (f.type === "base") return others.filter((r) => !isSeasonal(r));
    if (!datesOk) return [];
    return others.filter(
      (r) => isSeasonal(r) && dkey(r.startDate) <= f.end && f.start <= dkey(r.endDate)
    );
  }, [rules, f.type, f.start, f.end, datesOk, isEdit, rule]);

  const diff =
    room && Number(room.pricePerNight) > 0 && f.price !== ""
      ? ((num("price") - Number(room.pricePerNight)) / Number(room.pricePerNight)) * 100
      : null;

  const useRoomDefaults = () => {
    setF((p) => ({ ...p, ...seedForm(null, room), type: p.type, start: p.start, end: p.end }));
    setError("");
  };

  const submit = async (event) => {
    event.preventDefault();
    const message = validate();
    if (message) return setError(message);

    setSaving(true);
    setError("");

    try {
      const payload = {
        pricePerNight: num("price"),
        maxGuests: num("maxGuests"),
        adults: num("adults"),
        children: num("children"),
        extraGuestPrice: num("extra"),
      };

      if (f.type === "seasonal") {
        payload.startDate = f.start;
        payload.endDate = f.end;
      } else if (isEdit) {
        payload.startDate = null;
        payload.endDate = null;
      }

      const data = await apiRequest(isEdit ? `${basePath}/${rule._id}` : basePath, {
        method: isEdit ? "PATCH" : "POST",
        body: JSON.stringify(payload),
      });
      onSaved(data.pricing, mode);
    } catch (err) {
      setError(err.message || "Unable to save pricing rule.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="vp-formpage" ref={topRef}>
      <form
        onSubmit={submit}
        onKeyDown={(e) => {
          if (e.key === "Enter" && e.target.tagName === "INPUT") e.preventDefault();
        }}
      >
        <header className="vp-header">
          <div>
            <div className="vp-kicker">{isEdit ? "EDIT PRICING RULE" : rule ? "DUPLICATE PRICING RULE" : "NEW PRICING RULE"}</div>
            <h1>{isEdit ? "Edit pricing rule" : "Add pricing rule"}</h1>
            <p>{room?.roomName} · room default {money(room?.pricePerNight)} / night</p>
          </div>
          <button type="button" className="vp-btn secondary" onClick={onCancel} disabled={saving}>← Back to pricing</button>
        </header>

        {error && (
          <div className="vp-alert error" role="alert"><span>{error}</span><button type="button" onClick={() => setError("")}>×</button></div>
        )}

        <div className="vp-form-layout">
          <div className="vp-form-main">
            <section className="vp-card">
              <div className="vp-card-head">
                <h2>1. When does this price apply?</h2>
                <p>Use a base rate for normal days, and seasonal rules to override it for specific dates.</p>
              </div>

              <div className="vp-type-grid">
                <button type="button" className={`vp-type ${f.type === "base" ? "on" : ""}`} onClick={() => set("type", "base")}>
                  <span className="vp-type-icon">◎</span>
                  <strong>Base rate</strong>
                  <small>Applies to all dates unless a seasonal rule overrides it.</small>
                </button>
                <button type="button" className={`vp-type ${f.type === "seasonal" ? "on" : ""}`} onClick={() => set("type", "seasonal")}>
                  <span className="vp-type-icon">◷</span>
                  <strong>Seasonal / date range</strong>
                  <small>Special price for festivals, peak season, weekends of a season, etc.</small>
                </button>
              </div>

              {f.type === "seasonal" && (
                <div className="vp-grid two">
                  <div className="vp-field">
                    <label>Start date <span className="vp-req">*</span></label>
                    <input type="date" {...bind("start")} />
                  </div>
                  <div className="vp-field">
                    <label>End date <span className="vp-req">*</span></label>
                    <input type="date" min={f.start} {...bind("end")} />
                    {datesOk && <small>{nightsBetween(f.start, f.end)} night(s) covered</small>}
                  </div>
                </div>
              )}
            </section>

            <section className="vp-card">
              <div className="vp-card-head vp-card-head-row">
                <div>
                  <h2>2. Price & guests</h2>
                  <p>Nightly price and how many guests it includes.</p>
                </div>
                <button type="button" className="vp-link" onClick={useRoomDefaults}>Use room defaults</button>
              </div>

              <div className="vp-grid three">
                <div className="vp-field">
                  <label>Price per night (₹) <span className="vp-req">*</span></label>
                  <input type="number" min="0" step="any" placeholder="e.g. 4500" {...bind("price")} />
                </div>
                <div className="vp-field">
                  <label>Maximum guests <span className="vp-req">*</span></label>
                  <input type="number" min="1" {...bind("maxGuests")} />
                </div>
                <div className="vp-field">
                  <label>Extra guest price (₹)</label>
                  <input type="number" min="0" step="any" {...bind("extra")} />
                  <small>Charged per additional guest</small>
                </div>
                <div className="vp-field">
                  <label>Adults <span className="vp-req">*</span></label>
                  <input type="number" min="1" {...bind("adults")} />
                </div>
                <div className="vp-field">
                  <label>Children</label>
                  <input type="number" min="0" {...bind("children")} />
                </div>
              </div>
            </section>
          </div>

          <aside className="vp-form-side">
            <div className="vp-summary">
              <span className="vp-kicker">PREVIEW</span>
              <span className={`vp-badge ${f.type === "base" ? "base" : "seasonal"}`}>
                {f.type === "base" ? "Base rate" : "Seasonal"}
              </span>
              <strong className="vp-price-lg">{f.price === "" ? "₹0" : money(f.price)} <small>/ night</small></strong>

              {diff !== null && Math.abs(diff) >= 0.5 && (
                <p className={`vp-diff ${diff > 0 ? "up" : "down"}`}>
                  {diff > 0 ? "▲" : "▼"} {Math.abs(diff).toFixed(0)}% {diff > 0 ? "above" : "below"} room default
                </p>
              )}

              <ul className="vp-summary-list">
                <li><span>Applies to</span><b>{f.type === "base" ? "All dates" : datesOk ? `${fmtDate(f.start)} → ${fmtDate(f.end)}` : "Select dates"}</b></li>
                <li><span>Guests</span><b>{f.adults || 0} adults{Number(f.children) ? ` + ${f.children} children` : ""}</b></li>
                <li><span>Max guests</span><b>{f.maxGuests || 0}</b></li>
                <li><span>Extra guest</span><b>{money(f.extra)}</b></li>
              </ul>

              {conflicts.length > 0 && (
                <div className="vp-warning">
                  <strong>Heads up</strong>
                  <p>
                    {f.type === "base"
                      ? "Another active base rate already exists for this room."
                      : `This overlaps ${conflicts.length} other active rule${conflicts.length > 1 ? "s" : ""}: ${conflicts
                          .slice(0, 2)
                          .map((c) => `${fmtDate(dkey(c.startDate))} → ${fmtDate(dkey(c.endDate))}`)
                          .join(", ")}${conflicts.length > 2 ? "…" : ""}.`}
                  </p>
                </div>
              )}
            </div>

            <div className="vp-form-actions">
              <button type="button" className="vp-btn secondary" onClick={onCancel} disabled={saving}>Cancel</button>
              <button type="submit" className="vp-btn primary" disabled={saving}>
                {saving ? "Saving..." : isEdit ? "Save changes" : "Create rule"}
              </button>
            </div>
          </aside>
        </div>
      </form>
    </div>
  );
}

/* ---------- DETAIL PAGE ---------- */

function PricingDetail({ rule, room, conflicts, today, busy, onBack, onEdit, onDuplicate, onToggle, onDelete }) {
  const topRef = useRef(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    topRef.current?.scrollIntoView({ block: "start" });
  }, []);

  const state = ruleState(rule, today);
  const seasonal = isSeasonal(rule);
  const diff =
    room && Number(room.pricePerNight) > 0
      ? ((Number(rule.pricePerNight) - Number(room.pricePerNight)) / Number(room.pricePerNight)) * 100
      : null;

  const Item = ({ label, value }) => (
    <div className="vp-item"><small>{label}</small><strong>{value}</strong></div>
  );

  return (
    <div className="vp-formpage" ref={topRef}>
      <header className="vp-header">
        <div>
          <div className="vp-kicker">PRICING RULE DETAILS</div>
          <h1>{seasonal ? "Seasonal pricing rule" : "Base rate"}</h1>
          <p>{room?.roomName}</p>
        </div>
        <button className="vp-btn secondary" onClick={onBack}>← Back to pricing</button>
      </header>

      <section className="vp-card vp-detail">
        <div className="vp-detail-top">
          <div>
            <span className={`vp-badge ${seasonal ? "seasonal" : "base"}`}>{seasonal ? "Seasonal" : "Base rate"}</span>
            <span className={`vp-state ${state}`}>{STATE_LABEL[state]}</span>
            <strong className="vp-price-lg">{money(rule.pricePerNight)} <small>/ night</small></strong>
            {diff !== null && Math.abs(diff) >= 0.5 && (
              <p className={`vp-diff ${diff > 0 ? "up" : "down"}`}>
                {diff > 0 ? "▲" : "▼"} {Math.abs(diff).toFixed(0)}% {diff > 0 ? "above" : "below"} room default ({money(room.pricePerNight)})
              </p>
            )}
          </div>

          <div className="vp-detail-actions">
            {confirming ? (
              <div className="vp-confirm-inline">
                <span>Delete this rule permanently?</span>
                <button className="vp-btn danger" onClick={onDelete} disabled={busy}>{busy ? "Deleting..." : "Yes, delete"}</button>
                <button className="vp-btn secondary" onClick={() => setConfirming(false)} disabled={busy}>Cancel</button>
              </div>
            ) : (
              <>
                <button className="vp-btn primary" onClick={onEdit}>✎ Edit</button>
                <button className="vp-btn secondary" onClick={onDuplicate}>⧉ Duplicate</button>
                <button className="vp-btn secondary" onClick={onToggle} disabled={busy}>{rule.isActive ? "Disable" : "Enable"}</button>
                <button className="vp-btn danger-outline" onClick={() => setConfirming(true)}>Delete</button>
              </>
            )}
          </div>
        </div>

        {conflicts?.length > 0 && (
          <div className="vp-warning wide">
            <strong>Overlapping rules</strong>
            <p>
              This rule overlaps {conflicts.length} other active rule{conflicts.length > 1 ? "s" : ""}
              {" "}({conflicts.slice(0, 3).map((c) => (isSeasonal(c) ? `${fmtDate(dkey(c.startDate))} → ${fmtDate(dkey(c.endDate))}` : "Base rate")).join(", ")}).
              Consider disabling or changing one of them to avoid confusion.
            </p>
          </div>
        )}

        <div className="vp-detail-grid">
          <section>
            <h4>Applies to</h4>
            <div className="vp-items">
              <Item label="Type" value={seasonal ? "Seasonal / date range" : "Base rate (all dates)"} />
              <Item label="Start date" value={seasonal ? fmtDate(dkey(rule.startDate)) : "—"} />
              <Item label="End date" value={seasonal ? fmtDate(dkey(rule.endDate)) : "—"} />
              <Item label="Nights covered" value={seasonal ? nightsBetween(dkey(rule.startDate), dkey(rule.endDate)) : "All"} />
            </div>
          </section>

          <section>
            <h4>Price & guests</h4>
            <div className="vp-items">
              <Item label="Price per night" value={money(rule.pricePerNight)} />
              <Item label="Extra guest price" value={money(rule.extraGuestPrice)} />
              <Item label="Maximum guests" value={rule.maxGuests} />
              <Item label="Included guests" value={guestText(rule)} />
            </div>
          </section>
        </div>

        <p className="vp-muted vp-dates">Created {fmtDateTime(rule.createdAt)} · Last updated {fmtDateTime(rule.updatedAt)}</p>
      </section>
    </div>
  );
}

/* ---------- MAIN ---------- */

export default function VillaRoomPricing() {
  const [villas, setVillas] = useState([]);
  const [villaId, setVillaId] = useState(() => readSession(KEY_VILLA));
  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState(() => readSession(KEY_ROOM));
  const [booting, setBooting] = useState(true);
  const [roomsLoading, setRoomsLoading] = useState(false);

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [filter, setFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const [view, setView] = useState("list"); // list | form | detail
  const [formMode, setFormMode] = useState("create");
  const [selected, setSelected] = useState(null);

  const today = todayKey();
  const room = rooms.find((r) => r._id === roomId) || null;
  const villa = villas.find((v) => v._id === villaId) || null;

  const basePath = `/api/vendor/villas/${encodeURIComponent(villaId)}/rooms/${encodeURIComponent(roomId)}/pricing`;

  /* villas */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiRequest("/api/vendor/villas/my-villas");
        if (cancelled) return;
        const list = data.villas || data.data || [];
        setVillas(list);
        setVillaId((cur) => (cur && list.some((v) => v._id === cur) ? cur : list[0]?._id || ""));
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /* rooms */
  useEffect(() => {
    if (!villaId) { setRooms([]); setRoomId(""); return undefined; }
    writeSession(KEY_VILLA, villaId);
    let cancelled = false;
    setRoomsLoading(true);
    (async () => {
      try {
        const data = await apiRequest(`/api/vendor/villas/${encodeURIComponent(villaId)}/rooms`);
        if (cancelled) return;
        const list = Array.isArray(data.rooms) ? data.rooms : [];
        setRooms(list);
        setRoomId((cur) => (cur && list.some((r) => r._id === cur) ? cur : list[0]?._id || ""));
      } catch (err) {
        if (!cancelled) { setRooms([]); setRoomId(""); setError(err.message); }
      } finally {
        if (!cancelled) setRoomsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [villaId]);

  useEffect(() => {
    if (roomId) writeSession(KEY_ROOM, roomId);
  }, [roomId]);

  /* rules */
  const loadRules = useCallback(
    async (silent = false) => {
      if (!villaId || !roomId) return;
      if (!silent) setLoading(true);
      try {
        const data = await apiRequest(basePath);
        setRules(Array.isArray(data.pricing) ? data.pricing : []);
      } catch (err) {
        setError(err.message || "Unable to load pricing rules.");
      } finally {
        setLoading(false);
      }
    },
    [villaId, roomId, basePath]
  );

  useEffect(() => {
    setRules([]);
    setView("list");
    setSelected(null);
    setConfirmId(null);
    loadRules();
  }, [loadRules]);

  const conflictMap = useMemo(() => findConflicts(rules), [rules]);

  const decorated = useMemo(
    () => rules.map((r) => ({ rule: r, state: ruleState(r, today) })),
    [rules, today]
  );

  const counts = useMemo(() => {
    const c = { all: decorated.length, current: 0, upcoming: 0, expired: 0, disabled: 0 };
    decorated.forEach(({ state }) => {
      if (state === "always" || state === "live") c.current += 1;
      else c[state] += 1;
    });
    return c;
  }, [decorated]);

  const visible = useMemo(
    () =>
      decorated.filter(({ state }) => {
        if (filter === "all") return true;
        if (filter === "current") return state === "always" || state === "live";
        return state === filter;
      }),
    [decorated, filter]
  );

  /* price that applies today (seasonal overrides base) */
  const todaysRate = useMemo(() => {
    const newest = (list) =>
      [...list].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0];
    const live = decorated.filter((d) => d.state === "live").map((d) => d.rule);
    if (live.length) return { rule: newest(live), source: "Seasonal rule" };
    const base = decorated.filter((d) => d.state === "always").map((d) => d.rule);
    if (base.length) return { rule: newest(base), source: "Base rate" };
    return { rule: null, source: "Room default" };
  }, [decorated]);

  const seasonalCount = rules.filter(isSeasonal).length;

  /* actions */
  const openForm = (mode, rule = null) => {
    setError("");
    setNotice("");
    setSelected(rule);
    setFormMode(mode);
    setView("form");
  };

  const openDetail = (rule) => {
    setError("");
    setNotice("");
    setSelected(rule);
    setView("detail");
  };

  const backToList = () => {
    setView("list");
    setSelected(null);
  };

  const handleSaved = (saved, mode) => {
    if (saved?._id) {
      setRules((prev) =>
        mode === "edit" ? prev.map((r) => (r._id === saved._id ? saved : r)) : [...prev, saved]
      );
    }
    setNotice(mode === "edit" ? "Pricing rule updated successfully." : "Pricing rule created successfully.");
    backToList();
    loadRules(true);
  };

  const toggleActive = async (rule) => {
    setBusyId(rule._id);
    setError("");
    setNotice("");
    try {
      const data = await apiRequest(`${basePath}/${rule._id}`, {
        method: "PATCH",
        body: JSON.stringify({ isActive: !rule.isActive }),
      });
      const updated = data.pricing || { ...rule, isActive: !rule.isActive };
      setRules((prev) => prev.map((r) => (r._id === rule._id ? updated : r)));
      setSelected((cur) => (cur && cur._id === rule._id ? updated : cur));
      setNotice(`Pricing rule ${rule.isActive ? "disabled" : "enabled"}.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const deleteRule = async (rule) => {
    setBusyId(rule._id);
    setError("");
    setNotice("");
    try {
      await apiRequest(`${basePath}/${rule._id}`, { method: "DELETE" });
      setRules((prev) => prev.filter((r) => r._id !== rule._id));
      setConfirmId(null);
      setNotice("Pricing rule deleted successfully.");
      if (view === "detail") backToList();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  /* ---------- guards ---------- */

  if (booting) {
    return <div className="vp-page"><div className="vp-center"><span className="vp-spinner" /> Loading your villas...</div></div>;
  }

  if (!villas.length) {
    return (
      <div className="vp-page">
        {error && <div className="vp-alert error"><span>{error}</span><button onClick={() => setError("")}>×</button></div>}
        <div className="vp-empty">
          <div className="vp-empty-icon">⌂</div>
          <h3>Add a villa first</h3>
          <p>Pricing is managed per room. Create a villa and add rooms, then set rates here.</p>
        </div>
      </div>
    );
  }

  /* ---------- sub pages ---------- */

  if (view === "form") {
    return (
      <div className="vp-page">
        <PricingForm
          key={`${formMode}-${selected?._id || "new"}`}
          mode={formMode}
          rule={selected}
          room={room}
          rules={rules}
          basePath={basePath}
          onCancel={backToList}
          onSaved={handleSaved}
        />
      </div>
    );
  }

  if (view === "detail" && selected) {
    const current = rules.find((r) => r._id === selected._id) || selected;
    return (
      <div className="vp-page">
        {error && <div className="vp-alert error"><span>{error}</span><button onClick={() => setError("")}>×</button></div>}
        <PricingDetail
          key={current._id}
          rule={current}
          room={room}
          conflicts={conflictMap[current._id]}
          today={today}
          busy={busyId === current._id}
          onBack={backToList}
          onEdit={() => openForm("edit", current)}
          onDuplicate={() => openForm("create", current)}
          onToggle={() => toggleActive(current)}
          onDelete={() => deleteRule(current)}
        />
      </div>
    );
  }

  /* ---------- list page ---------- */

  return (
    <div className="vp-page">
      <header className="vp-header">
        <div>
          <div className="vp-kicker">PROPERTY MANAGEMENT / VILLAS</div>
          <h1>Rate plans & pricing</h1>
          <p>Set base rates and seasonal prices for each room.</p>
        </div>
        <div className="vp-header-actions">
          <button className="vp-btn secondary" onClick={() => loadRules()} disabled={loading || !roomId}>↻ Refresh</button>
          <button className="vp-btn primary" onClick={() => openForm("create")} disabled={!roomId}>＋ Add pricing rule</button>
        </div>
      </header>

      <section className="vp-controls">
        <div className="vp-control">
          <label htmlFor="vp-villa">Villa</label>
          <select id="vp-villa" value={villaId} onChange={(e) => setVillaId(e.target.value)}>
            {villas.map((v) => <option key={v._id} value={v._id}>{v.name}</option>)}
          </select>
        </div>
        <div className="vp-control">
          <label htmlFor="vp-room">Room</label>
          <select id="vp-room" value={roomId} onChange={(e) => setRoomId(e.target.value)} disabled={roomsLoading || !rooms.length}>
            {rooms.length === 0 && <option value="">{roomsLoading ? "Loading..." : "No rooms"}</option>}
            {rooms.map((r) => (
              <option key={r._id} value={r._id}>{r.roomName}{r.roomNumber ? ` (${r.roomNumber})` : ""}</option>
            ))}
          </select>
        </div>
      </section>

      {notice && <div className="vp-alert success"><span>✓ {notice}</span><button onClick={() => setNotice("")}>×</button></div>}
      {error && <div className="vp-alert error" role="alert"><span>{error}</span><button onClick={() => setError("")}>×</button></div>}

      {!rooms.length && !roomsLoading ? (
        <div className="vp-empty">
          <div className="vp-empty-icon">▭</div>
          <h3>No rooms in {villa?.name || "this villa"}</h3>
          <p>Add a room from “Villa Rooms” first, then come back to set its pricing.</p>
        </div>
      ) : (
        <>
          <section className="vp-stats">
            <div className="vp-stat highlight">
              <small>Rate applied today</small>
              <strong>{money(todaysRate.rule ? todaysRate.rule.pricePerNight : room?.pricePerNight)}</strong>
              <em>{todaysRate.source}</em>
            </div>
            <div className="vp-stat"><small>Total rules</small><strong>{rules.length}</strong></div>
            <div className="vp-stat"><small>Seasonal rules</small><strong>{seasonalCount}</strong></div>
            <div className="vp-stat"><small>Live now</small><strong>{counts.current}</strong></div>
            <div className="vp-stat"><small>Room default</small><strong>{money(room?.pricePerNight)}</strong></div>
          </section>

          <section className="vp-panel">
            <div className="vp-toolbar">
              <div>
                <h2>Pricing rules</h2>
                <p>{room?.roomName} · seasonal rules override the base rate on their dates</p>
              </div>
            </div>

            <div className="vp-filters">
              {[
                ["all", "All"], ["current", "Live / always on"], ["upcoming", "Upcoming"], ["expired", "Expired"], ["disabled", "Disabled"],
              ].map(([key, label]) => (
                <button key={key} className={filter === key ? "on" : ""} onClick={() => setFilter(key)}>
                  {label} <span>{counts[key]}</span>
                </button>
              ))}
            </div>

            {loading ? (
              <div className="vp-center"><span className="vp-spinner" /> Loading pricing rules...</div>
            ) : visible.length === 0 ? (
              <div className="vp-empty compact">
                <div className="vp-empty-icon">$</div>
                <h3>{rules.length ? "No rules match this filter" : "No pricing rules yet"}</h3>
                <p>{rules.length ? "Try a different filter." : "Add a base rate first, then seasonal rules for peak dates."}</p>
                {!rules.length && <button className="vp-btn primary" onClick={() => openForm("create")}>＋ Add first rule</button>}
              </div>
            ) : (
              <div className="vp-table-wrap">
                <table className="vp-table">
                  <thead>
                    <tr>
                      <th>Rule</th>
                      <th>Applies to</th>
                      <th className="num">Price / night</th>
                      <th>Guests</th>
                      <th className="num">Extra guest</th>
                      <th>Status</th>
                      <th className="right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map(({ rule, state }) => {
                      const seasonal = isSeasonal(rule);
                      const busy = busyId === rule._id;
                      const clash = conflictMap[rule._id];
                      return (
                        <tr key={rule._id} className={state === "expired" || state === "disabled" ? "muted" : ""}>
                          <td>
                            <span className={`vp-badge ${seasonal ? "seasonal" : "base"}`}>{seasonal ? "Seasonal" : "Base rate"}</span>
                            {clash && <span className="vp-clash" title="Overlaps with another active rule">⚠ Overlap</span>}
                          </td>
                          <td>
                            {seasonal ? (
                              <div className="vp-range">
                                <strong>{fmtDate(dkey(rule.startDate))} → {fmtDate(dkey(rule.endDate))}</strong>
                                <small>{nightsBetween(dkey(rule.startDate), dkey(rule.endDate))} night(s)</small>
                              </div>
                            ) : (
                              <div className="vp-range"><strong>All dates</strong><small>Default nightly rate</small></div>
                            )}
                          </td>
                          <td className="num vp-price">{money(rule.pricePerNight)}</td>
                          <td>
                            <div className="vp-range"><strong>{guestText(rule)}</strong><small>Max {rule.maxGuests} guests</small></div>
                          </td>
                          <td className="num">{Number(rule.extraGuestPrice) ? money(rule.extraGuestPrice) : "—"}</td>
                          <td><span className={`vp-state ${state}`}>{STATE_LABEL[state]}</span></td>
                          <td>
                            {confirmId === rule._id ? (
                              <div className="vp-actions confirm">
                                <span>Delete this rule?</span>
                                <button className="danger-solid" onClick={() => deleteRule(rule)} disabled={busy}>{busy ? "..." : "Yes"}</button>
                                <button onClick={() => setConfirmId(null)} disabled={busy}>No</button>
                              </div>
                            ) : (
                              <div className="vp-actions">
                                <button onClick={() => openDetail(rule)} disabled={busy}>View</button>
                                <button onClick={() => openForm("edit", rule)} disabled={busy}>Edit</button>
                                <button onClick={() => openForm("create", rule)} disabled={busy}>Duplicate</button>
                                <button onClick={() => toggleActive(rule)} disabled={busy}>{rule.isActive ? "Disable" : "Enable"}</button>
                                <button className="danger" onClick={() => setConfirmId(rule._id)} disabled={busy}>Delete</button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}